import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerModal({ open, onOpenChange }) {
  const [form, setForm] = useState({ venue_name: "", contact_name: "", email: "", phone: "", message: "" });
  const [consentTerms, setConsentTerms] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.venue_name || !form.contact_name || !form.email || !form.phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!consentTerms) {
      toast.error("Please agree to the Terms & Conditions and Privacy Policy");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/partners`, form);
      toast.success("Inquiry sent! Our team will get back to you within 48 hours.");
      setForm({ venue_name: "", contact_name: "", email: "", phone: "", message: "" });
      setConsentTerms(false);
      setConsentMarketing(false);
      onOpenChange(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border/40 max-h-[90vh] overflow-y-auto" data-testid="partner-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight font-medium">
            Partner with Cita Rush
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Tell us about your venue and we'll get back to you within 48 hours.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">Venue name *</label>
            <Input
              value={form.venue_name}
              onChange={(e) => setForm({ ...form, venue_name: e.target.value })}
              placeholder="Your cafe, bar, or restaurant name"
              className="rounded-sm"
              data-testid="partner-venue-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">Contact name *</label>
            <Input
              value={form.contact_name}
              onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              placeholder="Your full name"
              className="rounded-sm"
              data-testid="partner-contact-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">Email *</label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="venue@example.com"
              className="rounded-sm"
              data-testid="partner-email-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">Phone *</label>
            <Input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+91 9876543210"
              className="rounded-sm"
              data-testid="partner-phone-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">Message (optional)</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell us about your space, capacity, and availability..."
              rows={3}
              className="flex w-full rounded-sm border border-input bg-transparent px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
              data-testid="partner-message-input"
            />
          </div>

          <div className="space-y-3 pt-1" data-testid="partner-consent-checkboxes">
            <div className="flex items-start gap-3">
              <Checkbox
                id="partner-terms"
                checked={consentTerms}
                onCheckedChange={(checked) => setConsentTerms(!!checked)}
                className="mt-0.5"
                data-testid="partner-consent-terms-checkbox"
              />
              <label htmlFor="partner-terms" className="text-sm leading-relaxed text-muted-foreground cursor-pointer">
                I agree to the{" "}
                <a href="/terms" target="_blank" className="text-foreground underline underline-offset-2 hover:text-[hsl(var(--accent))] transition-colors">
                  Terms & Conditions
                </a>{" "}
                and{" "}
                <a href="/privacy" target="_blank" className="text-foreground underline underline-offset-2 hover:text-[hsl(var(--accent))] transition-colors">
                  Privacy Policy
                </a>. *
              </label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox
                id="partner-marketing"
                checked={consentMarketing}
                onCheckedChange={(checked) => setConsentMarketing(!!checked)}
                className="mt-0.5"
                data-testid="partner-consent-marketing-checkbox"
              />
              <label htmlFor="partner-marketing" className="text-sm leading-relaxed text-muted-foreground cursor-pointer">
                I agree to receive event updates and promotional communication from Cita Rush.
              </label>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading || !consentTerms}
            className="w-full rounded-sm px-8 py-5 text-base font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5"
            data-testid="partner-submit-btn"
          >
            {loading ? "Sending..." : "Submit inquiry"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
